import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { motion } from "framer-motion";
import { colors } from "./colors";
import { PageContainer } from "./PageContainer";

/** Copied to dist/WorldMap.svg by webpack — must be a URL string for SSR. */
const WORLD_MAP_SRC = "/dist/WorldMap.svg?v=noborder";

const MAP_VIEWBOX = { w: 542, h: 236 };
const SUCCESS = colors.state.success.main;

const SERVERS = [
  { name: "Frankfurt", id: "eu-central-1", mapId: "DE", active: true },
  { name: "London", id: "eu-west-2", mapId: "GB", active: true },
  { name: "Stockholm", id: "eu-north-1", mapId: "SE", active: false },
  { name: "Ohio", id: "us-east-2", point: { x: 150, y: 74 }, active: true },
  { name: "California", id: "us-west-1", point: { x: 103, y: 76 }, active: false },
  { name: "São Paulo", id: "sa-east-1", mapId: "BR", active: false },
  { name: "Singapore", id: "ap-southeast-1", mapId: "SG", active: false },
];

const NETWORK_LINKS = [
  ["London", "Frankfurt"],
  ["Frankfurt", "Ohio"],
];

const MotionBox = motion.create(Box);

const SECTION_BG = "#080808";

const MAP_FRAME_SX = {
  mt: 2,
  width: "100%",
  overflow: "visible",
  borderRadius: "18px",
  position: "relative",
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const STATS_ROW_SX = {
  position: "relative",
  display: { xs: "grid", md: "flex" },
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))" },
  flexDirection: { md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  gap: { xs: 0, md: 0 },
};

const STATS_BAR_SX = {
  mt: { xs: 3, md: 4 },
  position: "relative",
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.06)",
  background: "rgba(14,14,16,0.55)",
  backdropFilter: "blur(12px)",
  overflow: "hidden",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
};

const STAT_CARD_SX = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 1.25, md: 1.5 },
  px: { xs: 1.5, md: 2.25 },
  py: { xs: "1.35rem", md: "1.65rem" },
  flex: { md: 1 },
  minWidth: 0,
  position: "relative",
  transition: "transform 280ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
};

const STAT_SEPARATOR_SX = {
  position: "relative",
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "center",
  width: "1px",
  flexShrink: 0,
  pointerEvents: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "28%",
    background: "linear-gradient(to bottom, transparent, rgba(8,8,8,0.9), transparent)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "28%",
    background: "linear-gradient(to top, transparent, rgba(8,8,8,0.9), transparent)",
  },
};

const STAT_ICON_WRAP_SX = {
  color: colors.primary.main,
  flexShrink: 0,
  filter: "drop-shadow(0 0 12px rgba(232,163,61,0.22))",
};

const STAT_VALUE_SX = {
  fontSize: { xs: "1.65rem", md: "1.85rem" },
  fontWeight: 700,
  letterSpacing: "-0.03em",
  lineHeight: 1,
  color: colors.text.primary,
  mb: 0.35,
};

const STAT_LABEL_SX = {
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: colors.primary.main,
  mb: 0.4,
};

const STAT_TEXT_SX = {
  fontSize: { xs: "0.72rem", md: "0.76rem" },
  lineHeight: 1.55,
  fontWeight: 500,
  color: colors.text.secondary,
};

const STATS = [
  {
    key: "active",
    icon: PublicOutlinedIcon,
    stat: "3",
    title: "Active regions",
    text: "Live and supporting competitive play.",
  },
  {
    key: "building",
    icon: null,
    stat: "4",
    title: "Building regions",
    text: "Spinning up as player density grows.",
  },
  {
    key: "verified",
    icon: ShieldOutlinedIcon,
    stat: "100%",
    title: "Verified players",
    text: "Every account on every server passes human review.",
  },
  {
    key: "dedicated",
    icon: VerifiedUserOutlinedIcon,
    stat: "100%",
    title: "Dedicated infrastructure",
    text: "Built for competitive integrity and long-term scale.",
  },
];

const TOOLTIP_PAPER_STYLE = {
  position: "relative",
  overflow: "hidden",
  maxWidth: "10rem",
  padding: "0.75rem 0.95rem",
  borderRadius: "16px",
  background: "linear-gradient(180deg, rgba(14,14,16,0.96) 0%, rgba(8,8,10,0.985) 100%)",
  border: "1px solid rgba(255,255,255,0.06)",
  color: colors.text.primary,
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "0.775rem",
  fontWeight: "500",
  backdropFilter: "blur(18px)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.04), 0 16px 42px rgba(0,0,0,0.52), 0 0 24px rgba(232,163,61,0.06)",
  letterSpacing: "0.02em",
  transformOrigin: "center top",
};

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function InactiveMarkerIcon({ size = 12, borderColor = colors.text.muted, dotColor = "rgba(115, 115, 125, 0.55)" }) {
  const dotSize = Math.max(3, Math.round(size * 0.42));

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: "50%",
        border: `1px dashed ${borderColor}`,
        flexShrink: 0,
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          width: dotSize,
          height: dotSize,
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          backgroundColor: dotColor,
        },
      }}
    />
  );
}

function BuildingDensityIcon() {
  return (
    <InactiveMarkerIcon
      size={40}
      borderColor={colors.primary.main}
      dotColor={colors.primary.main}
    />
  );
}

function StatSeparator() {
  return (
    <Box sx={STAT_SEPARATOR_SX}>
      <Box
        sx={{
          position: "relative",
          width: "1px",
          height: "88px",
          background: `
            linear-gradient(
              to bottom,
              transparent 0%,
              rgba(255,255,255,0.05) 18%,
              rgba(232,163,61,0.16) 50%,
              rgba(255,255,255,0.05) 82%,
              transparent 100%
            )
          `,
        }}
      >
        <MotionBox
          aria-hidden
          animate={{ opacity: [0.35, 0.7, 0.35], scaleY: [1, 1.12, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "8px",
            height: "44px",
            borderRadius: "999px",
            background: "radial-gradient(ellipse at center, rgba(232,163,61,0.34), transparent 72%)",
            filter: "blur(10px)",
          }}
        />
      </Box>
    </Box>
  );
}

function StatCard({ item, index }) {
  const Icon = item.icon;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.85, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      sx={STAT_CARD_SX}
    >
      <Box sx={STAT_ICON_WRAP_SX}>
        {Icon ? (
          <Icon sx={{ fontSize: { xs: "2.25rem", md: "2.5rem" } }} />
        ) : (
          <BuildingDensityIcon />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={STAT_VALUE_SX}>{item.stat}</Typography>
        <Typography sx={STAT_LABEL_SX}>{item.title}</Typography>
        <Typography sx={STAT_TEXT_SX}>{item.text}</Typography>
      </Box>
    </MotionBox>
  );
}

function LegendItem({ variant, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      {variant === "active" ? (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: SUCCESS,
            boxShadow: "0 0 15px rgba(34, 197, 94, 0.35)",
          }}
        />
      ) : (
        <InactiveMarkerIcon size={12} />
      )}
      <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, letterSpacing: "0.02em" }}>
        {label}
      </Typography>
    </Box>
  );
}

function getMapPoint(svg, server) {
  if (server.point) {
    return { x: server.point.x, y: server.point.y };
  }

  const el = svg.querySelector(`#${server.mapId}`);
  if (!el) return null;

  const box = el.getBBox();
  const [fx, fy] = server.anchor || [0.5, 0.5];

  return {
    x: box.x + box.width * fx,
    y: box.y + box.height * fy,
  };
}

function getMapAnchor(svg, server) {
  const point = getMapPoint(svg, server);
  if (!point) return null;

  return {
    left: `${(point.x / MAP_VIEWBOX.w) * 100}%`,
    top: `${(point.y / MAP_VIEWBOX.h) * 100}%`,
  };
}

function createMarkerEl(server) {
  const root = document.createElement("button");
  root.type = "button";
  root.setAttribute("aria-label", `${server.name} — ${server.active ? "Active" : "Building density"}`);
  Object.assign(root.style, {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: "22px",
    height: "22px",
    padding: "0",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    zIndex: "2",
  });

  if (server.active) {
    const ring = document.createElement("span");
    Object.assign(ring.style, {
      position: "absolute",
      inset: "2px",
      borderRadius: "50%",
      border: `1.5px solid ${SUCCESS}`,
      animation: "serverMarkerPulse 3.6s ease-out infinite",
      pointerEvents: "none",
    });

    const core = document.createElement("span");
    Object.assign(core.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: "8px",
      height: "8px",
      transform: "translate(-50%, -50%)",
      borderRadius: "50%",
      backgroundColor: SUCCESS,
      boxShadow: "0 0 8px rgba(34, 197, 94, 0.4)",
      pointerEvents: "none",
    });

    const dot = document.createElement("span");
    Object.assign(dot.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: "3px",
      height: "3px",
      transform: "translate(-50%, -50%)",
      borderRadius: "50%",
      backgroundColor: SECTION_BG,
      pointerEvents: "none",
    });

    root.append(ring, core, dot);
  } else {
    const ring = document.createElement("span");
    Object.assign(ring.style, {
      position: "absolute",
      inset: "3px",
      borderRadius: "50%",
      border: `1px dashed ${colors.text.muted}`,
      pointerEvents: "none",
    });

    const core = document.createElement("span");
    Object.assign(core.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: "5px",
      height: "5px",
      transform: "translate(-50%, -50%)",
      borderRadius: "50%",
      backgroundColor: "rgba(115, 115, 125, 0.55)",
      pointerEvents: "none",
    });

    root.append(ring, core);
  }

  return root;
}

function createTooltipEl() {
  const tooltip = document.createElement("div");
  Object.assign(tooltip.style, {
    position: "absolute",
    zIndex: "5",
    pointerEvents: "none",
    opacity: "0",
    transform: "translate(-50%, calc(-100% - 10px)) scale(0.96)",
    filter: "blur(4px)",
    transition: "opacity 0.22s cubic-bezier(0.22, 1, 0.36, 1), transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), filter 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
    ...TOOLTIP_PAPER_STYLE,
  });

  const topLine = document.createElement("span");
  Object.assign(topLine.style, {
    position: "absolute",
    left: "10%",
    right: "10%",
    top: "0",
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
    pointerEvents: "none",
  });

  const glow = document.createElement("span");
  Object.assign(glow.style, {
    position: "absolute",
    width: "10rem",
    height: "10rem",
    top: "-4rem",
    right: "-3rem",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(232,163,61,0.22), transparent 72%)",
    filter: "blur(34px)",
    pointerEvents: "none",
    animation: "tooltipGlowPulse 5s ease-in-out infinite",
  });

  const content = document.createElement("div");
  Object.assign(content.style, {
    position: "relative",
    zIndex: "2",
  });

  tooltip.append(topLine, glow, content);
  tooltip._content = content;
  return tooltip;
}

function fillTooltip(tooltip, server) {
  const content = tooltip._content;
  content.replaceChildren();

  const name = document.createElement("div");
  name.textContent = server.name;
  Object.assign(name.style, {
    fontWeight: "500",
    letterSpacing: "0.04em",
    lineHeight: "1.4",
    color: colors.text.primary,
    marginBottom: "4px",
  });

  const region = document.createElement("div");
  region.textContent = server.id;
  Object.assign(region.style, {
    fontSize: "0.6875rem",
    color: colors.text.muted,
    marginBottom: "8px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    letterSpacing: "0.02em",
  });

  const badge = document.createElement("span");
  badge.textContent = server.active ? "Active" : "Building density";
  Object.assign(badge.style, {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "0.625rem",
    fontWeight: "600",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: server.active ? SUCCESS : colors.text.muted,
    backgroundColor: server.active ? "rgba(34, 197, 94, 0.12)" : "rgba(115, 115, 125, 0.12)",
    border: server.active ? "1px solid rgba(34, 197, 94, 0.25)" : `1px dashed ${colors.text.muted}`,
  });

  content.append(name, region, badge);
}

function showTooltip(tooltip, el) {
  tooltip.style.left = el.style.left;
  tooltip.style.top = el.style.top;
  tooltip.style.opacity = "1";
  tooltip.style.transform = "translate(-50%, calc(-100% - 10px)) scale(1)";
  tooltip.style.filter = "blur(0px)";
}

function hideTooltip(tooltip) {
  tooltip.style.opacity = "0";
  tooltip.style.transform = "translate(-50%, calc(-100% - 10px)) scale(0.96)";
  tooltip.style.filter = "blur(4px)";
}

function layoutMarkers(svg, markerEls) {
  SERVERS.forEach((server, index) => {
    const pos = getMapAnchor(svg, server);
    const el = markerEls[index];
    if (!pos || !el) return;
    el.style.left = pos.left;
    el.style.top = pos.top;
  });
}

function createNetworkOverlay() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${MAP_VIEWBOX.w} ${MAP_VIEWBOX.h}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.setAttribute("aria-hidden", "true");
  Object.assign(svg.style, {
    position: "absolute",
    inset: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: "1",
    overflow: "visible",
  });

  const lines = NETWORK_LINKS.map(() => {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("stroke", "rgba(232,163,61,0.16)");
    line.setAttribute("stroke-width", "1");
    line.setAttribute("stroke-dasharray", "4 6");
    line.setAttribute("stroke-linecap", "round");
    line.style.animation = "networkDashFlow 32s linear infinite";
    svg.appendChild(line);
    return line;
  });

  return { svg, lines };
}

function layoutNetworkLines(mapSvg, lines) {
  const serverByName = Object.fromEntries(SERVERS.map((server) => [server.name, server]));

  NETWORK_LINKS.forEach(([fromName, toName], index) => {
    const from = getMapPoint(mapSvg, serverByName[fromName]);
    const to = getMapPoint(mapSvg, serverByName[toName]);
    const line = lines[index];
    if (!from || !to || !line) return;

    line.setAttribute("x1", String(from.x));
    line.setAttribute("y1", String(from.y));
    line.setAttribute("x2", String(to.x));
    line.setAttribute("y2", String(to.y));
  });
}

export default function ServerMapSection() {
  const mapFrameRef = useRef(null);
  const mapHostRef = useRef(null);
  const networkHostRef = useRef(null);
  const markersHostRef = useRef(null);
  const markerElsRef = useRef([]);
  const networkLinesRef = useRef([]);
  const cleanedUpRef = useRef(false);

  useEffect(() => {
    cleanedUpRef.current = false;
    const mapHost = mapHostRef.current;
    const networkHost = networkHostRef.current;
    const markersHost = markersHostRef.current;
    if (!mapHost || !networkHost || !markersHost) return undefined;

    const { svg: networkSvg, lines: networkLines } = createNetworkOverlay();
    networkHost.appendChild(networkSvg);
    networkLinesRef.current = networkLines;

    const tooltip = createTooltipEl();
    markersHost.appendChild(tooltip);

    const markerEls = SERVERS.map((server) => {
      const el = createMarkerEl(server);

      el.addEventListener("mouseenter", () => {
        fillTooltip(tooltip, server);
        showTooltip(tooltip, el);
      });

      el.addEventListener("mouseleave", () => {
        hideTooltip(tooltip);
      });

      markersHost.appendChild(el);
      return el;
    });
    markerElsRef.current = markerEls;

    const positionOverlay = () => {
      const svg = mapHost.querySelector("svg");
      if (!svg) return;
      layoutMarkers(svg, markerEls);
      layoutNetworkLines(svg, networkLines);
    };

    fetch(WORLD_MAP_SRC)
      .then((res) => res.text())
      .then((svgText) => {
        if (cleanedUpRef.current) return;
        mapHost.innerHTML = svgText;
        const svg = mapHost.querySelector("svg");
        if (svg) {
          svg.setAttribute("width", "100%");
          svg.style.display = "block";
          svg.style.height = "auto";
          svg.style.pointerEvents = "none";
          svg.style.userSelect = "none";
        }
        positionOverlay();
      })
      .catch(() => {});

    const resizeObserver = new ResizeObserver(positionOverlay);
    resizeObserver.observe(mapFrameRef.current);

    return () => {
      cleanedUpRef.current = true;
      resizeObserver.disconnect();
      markerEls.forEach((el) => el.remove());
      tooltip.remove();
      networkSvg.remove();
      mapHost.innerHTML = "";
      markerElsRef.current = [];
      networkLinesRef.current = [];
    };
  }, []);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "80px", md: "120px" },
        px: { xs: 2, md: 4 },
        backgroundColor: SECTION_BG,
        "@keyframes serverMarkerPulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 0.4 },
          "50%": { transform: "scale(1.35)", opacity: 0 },
        },
        "@keyframes tooltipGlowPulse": {
          "0%, 100%": { opacity: 0.14, transform: "scale(1)" },
          "50%": { opacity: 0.24, transform: "scale(1.06)" },
        },
        "@keyframes networkDashFlow": {
          from: { strokeDashoffset: "0" },
          to: { strokeDashoffset: "-40" },
        },
      }}
    >
      <PageContainer sx={{ maxWidth: "75rem" }}>
        <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: { xs: 4, lg: 6 },
              alignItems: "end",
              mb: { xs: 5, md: 7 },
            }}
          >
            <Box>
              <Eyebrow>Server infrastructure</Eyebrow>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.25rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.95,
                  textTransform: "uppercase",
                  color: colors.text.primary,
                  maxWidth: "24rem",
                  "& span": {
                    background: "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  },
                }}
              >
                Every region
                <br />
                <span>earns its place.</span>
              </Typography>
            </Box>

            <Box sx={{ maxWidth: "26rem", justifySelf: { lg: "end" } }}>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.8,
                  color: colors.text.secondary,
                  mb: 3,
                }}
              >
                We expand where player density can sustain
                fair queues, low latency, and competitive integrity.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                <LegendItem variant="active" label="Active" />
                <LegendItem variant="building" label="Building density" />
              </Box>
            </Box>
          </Box>
        </MotionBox>

        <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
        <Box ref={mapFrameRef} sx={MAP_FRAME_SX}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",

              background:
                "radial-gradient(ellipse at center bottom, transparent 55%, rgba(0,0,0,.25) 100%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `
              radial-gradient(
                circle at 55% 35%,
                rgba(232,163,61,.08),
                transparent 45%
              )
            `,
              filter: "blur(70px)",
              transform: "scale(1.2)",
              pointerEvents: "none",
            }}
          />
          <Box ref={mapHostRef} sx={{ lineHeight: 0 }} />
          <Box
            ref={networkHostRef}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          />
          <Box
            ref={markersHostRef}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 2,
              "& > button": { pointerEvents: "auto" },
            }}
          />
        </Box>
        </MotionBox>

        <Box sx={STATS_BAR_SX}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "radial-gradient(ellipse at center, rgba(232,163,61,0.05), transparent 72%)",
              filter: "blur(60px)",
              pointerEvents: "none",
            }}
          />
          <Box sx={STATS_ROW_SX}>
            {STATS.map((item, index) => (
              <React.Fragment key={item.key}>
                <StatCard item={item} index={index} />
                {index < STATS.length - 1 && <StatSeparator />}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
